/**
 * Tiny URL helpers — Phase 7.UI.15j.
 *
 * `appendQueryParams(url, params)` safely adds (or replaces) query-string
 * parameters on a URL while preserving all existing params. Designed for
 * appending `locale=en|de` to CopeCart checkout and management URLs
 * without breaking existing `metadata`, `affiliate_id`, tracking or any
 * future params.
 *
 * Edge cases handled:
 *   • URL with no query: returns base + "?key=value"
 *   • URL with existing query: appends with "&"
 *   • Existing key in URL: replaces its value (no duplicate key)
 *   • Falsy value (undefined/null/empty): the key is REMOVED from the URL
 *   • Hash fragment (#…): preserved at the end
 *   • Relative URLs and absolute URLs both work (falls back to manual
 *     splitting when `new URL()` throws on relative input)
 */
export function appendQueryParams(url, params) {
  if (!url || !params) return url;
  let absolute = true;
  let urlObj;
  try {
    urlObj = new URL(url);
  } catch {
    // Relative URL fallback — `new URL()` requires a base.
    absolute = false;
    try {
      urlObj = new URL(url, "https://placeholder.local");
    } catch {
      return url;
    }
  }
  const sp = urlObj.searchParams;
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") {
      sp.delete(k);
    } else {
      sp.set(k, String(v));
    }
  }
  if (absolute) return urlObj.toString();
  // Strip the placeholder origin for relative URLs.
  return urlObj.pathname + (urlObj.search ? urlObj.search : "") + (urlObj.hash || "");
}

/**
 * Convenience wrapper for CopeCart URLs: defaults locale to "en" when
 * an explicit locale isn't passed. We always pass SOMETHING because
 * (a) Stockaily's default UI language is English and (b) CopeCart's
 * default is browser-derived which can flip a Stockaily-EN user into
 * the German checkout flow.
 */
export function withCopecartLocale(url, locale) {
  const safeLocale = (locale || "en").toLowerCase() === "de" ? "de" : "en";
  return appendQueryParams(url, { locale: safeLocale });
}
